import { needsConfirmation } from '@contracts/users/CognitoStatus';
import AddRounded from '@mui/icons-material/AddRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import Edit from '@mui/icons-material/Edit';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import RestoreRounded from '@mui/icons-material/RestoreRounded';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { ButtonLink } from '@src/common/components/button/ButtonLink';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { IconButton } from '@src/common/components/IconButton';
import { LoadingPage } from '@src/common/components/LoadingPage';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import type { User } from '@src/manageUsers/domain/User';
import { useUserDeactivation } from '@src/manageUsers/hooks/useUserDeactivation';
import { useUsers } from '@src/manageUsers/hooks/useUsers';
import { UsersNavigation } from '@src/routing/UsersNavigation';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const ManageUsers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userList, isLoading } = useUsers();
  const {
    deactivateUser,
    reactivateUser,
    confirmCognitoUser,
    isLoading: isDeactivating,
  } = useUserDeactivation();
  const listId = useId();

  if (isLoading) {
    return <LoadingPage />;
  }

  const navigateToUserPage = (userId: string) => {
    navigate(UsersNavigation.edit.url(userId));
  };

  const columns: GridColDef[] = [
    { field: 'user', headerName: t('user'), flex: 1 },
    { field: 'email', headerName: t('email'), flex: 1 },
    { field: 'extension', headerName: t('extension'), flex: 1 },
    { field: 'cognitoStatus', headerName: t('status'), flex: 1 },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => {
        const isInactive = (isInactiveString: string) =>
          isInactiveString.toLowerCase() === 'true';

        return (
          <Stack direction="row" flex={1}>
            <IconButton
              onClick={() => navigateToUserPage(params.row.id)}
              title={t('edit')}
            >
              <Edit />
            </IconButton>
            {needsConfirmation(params.row.cognitoStatus) && (
              <IconButton
                disabled={isDeactivating}
                onClick={() => confirmCognitoUser(params.row.id)}
                title={t('confirm_user')}
              >
                <CheckCircleRounded color="success" />
              </IconButton>
            )}
            <IconButton
              disabled={isDeactivating}
              onClick={() =>
                isInactive(params.row.isInactive)
                  ? reactivateUser(params.row.id)
                  : deactivateUser(params.row.id)
              }
              title={
                isInactive(params.row.isInactive)
                  ? t('activate')
                  : t('deactivate')
              }
            >
              {isInactive(params.row.isInactive) ? (
                <RestoreRounded color="secondary" />
              ) : (
                <PersonOffOutlinedIcon color="error" />
              )}
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const rows = userList.users.map((user: User) => {
    return {
      id: user.id,
      user: user.name,
      email: user.email,
      extension: user.extension,
      cognitoStatus: user.cognitoStatus,
      isInactive: `${user.isInactive}`,
    };
  });

  return (
    <PageLayout
      title={t('users')}
      id={listId}
      rightBlock={
        <ButtonLink to="add" startIcon={<AddRounded />}>
          {t('add')}
        </ButtonLink>
      }
    >
      <FilterableTable
        ariaLabel="users-list"
        columns={columns}
        rows={rows}
        paginationAndSortMode="client"
      />
    </PageLayout>
  );
};
